import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Check } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type {
  ConstantinePatchOption,
  PatchSelection,
} from '@/sections/poe/types/poe.types';
import {
  useClearPatch,
  useCreatePR,
  useSelectPatch,
} from '@/sections/vulnerabilities/hooks/actions/useRemediation';
import { useOsintContext } from '@/state/OsintContext';
import { cn } from '@/utils/classname';

interface PatchOptionsPanelProps {
  validOptions: ConstantinePatchOption[];
  skippedCount: number;
  riskKey?: string;
  findingId: string;
  patches?: PatchSelection[];
  failureReason?: string;
}

export function PatchOptionsPanel({
  validOptions,
  skippedCount,
  riskKey,
  findingId,
  patches,
  failureReason,
}: PatchOptionsPanelProps) {
  const { isOsint } = useOsintContext();
  const { selectPatch, isLoading: isSelecting } = useSelectPatch();
  const { createPR, isLoading: isCreatingPR } = useCreatePR();
  const { clearPatch, isLoading: isClearing } = useClearPatch();
  const [expandedDiffs, setExpandedDiffs] = useState<Record<number, boolean>>(
    {}
  );
  const [createFailed, setCreateFailed] = useState(false);

  const persistedSelection = patches?.find(p => p.finding_id === findingId);

  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(
    persistedSelection?.option_id ?? null
  );
  const [prLink, setPrLink] = useState<string | null>(
    persistedSelection?.pr_link ?? null
  );

  // Sync local state when persisted selection changes (e.g. after cache refresh)
  useEffect(() => {
    setSelectedOptionId(persistedSelection?.option_id ?? null);
    setPrLink(persistedSelection?.pr_link ?? null);
  }, [persistedSelection?.option_id, persistedSelection?.pr_link]);

  if (validOptions.length === 0) {
    if (!failureReason && skippedCount === 0) return null;
    return (
      <div className="border-chariot-stroke overflow-hidden rounded-lg border">
        <div className="bg-chariot-background-secondary flex items-center justify-between px-4 py-3">
          <h5 className="text-chariot-text-primary flex items-center gap-2 text-sm font-medium">
            <DocumentTextIcon className="size-4" />
            Patch Generation
          </h5>
          {skippedCount > 0 && (
            <span className="text-chariot-status-critical text-xs">
              {skippedCount} option{skippedCount !== 1 ? 's' : ''} failed to
              apply
            </span>
          )}
        </div>
        {failureReason && (
          <div className="border-chariot-stroke border-t px-4 py-3">
            <div className="flex items-start gap-2 rounded-md border border-amber-500/20 bg-amber-500/5 p-3">
              <InformationCircleIcon className="mt-0.5 size-4 shrink-0 text-amber-500" />
              <p className="text-chariot-text-secondary text-sm">
                {failureReason}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  const handleSelect = async (
    option: ConstantinePatchOption,
    index: number
  ) => {
    if (!riskKey) return;
    const optionId = option.id ?? `option-${index}`;
    await selectPatch({
      riskKey,
      findingId,
      optionId,
      strategy: option.strategy ?? '',
    });
    setSelectedOptionId(optionId);
  };

  const handleCreatePR = async () => {
    if (!riskKey) return;
    setCreateFailed(false);
    const url = await createPR({ riskKey, findingId });
    if (url) {
      setPrLink(url);
    } else {
      setCreateFailed(true);
    }
  };

  const parseFilePaths = (diff: string): string[] => {
    const matches = [...diff.matchAll(/^\+\+\+\s+[ab]\/(.+)$/gm)];
    return matches.map(m => m[1]).filter(Boolean);
  };

  const isGitHubRisk = (() => {
    if (!riskKey || isOsint) return false;
    try {
      const parts = riskKey.split('#').filter(Boolean);
      const dns = parts.length >= 2 ? parts[1] : '';
      const url = new URL(dns.startsWith('http') ? dns : `https://${dns}`);
      return url.hostname === 'github.com';
    } catch {
      return false;
    }
  })();

  return (
    <div className="border-chariot-stroke overflow-hidden rounded-lg border">
      <div className="bg-chariot-background-secondary flex items-center justify-between px-4 py-3">
        <h5 className="text-chariot-text-primary flex items-center gap-2 text-sm font-medium">
          <DocumentTextIcon className="size-4" />
          Patch Options
        </h5>
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-secondary text-xs">
            {validOptions.length} option
            {validOptions.length !== 1 ? 's' : ''}
          </span>
          {skippedCount > 0 && (
            <span className="text-chariot-status-critical text-xs">
              ({skippedCount} failed to apply)
            </span>
          )}
        </div>
      </div>

      <Tabs defaultValue="0" className="w-full">
        <div className="border-chariot-stroke border-b px-4">
          <TabsList className="h-auto gap-0 rounded-none bg-transparent p-0">
            {validOptions.map((option: ConstantinePatchOption, i: number) => {
              const optionId = option.id ?? `option-${i}`;
              const isSelected = selectedOptionId === optionId;
              return (
                <TabsTrigger
                  key={i}
                  value={String(i)}
                  className="text-chariot-text-secondary data-[state=active]:text-chariot-text-primary data-[state=active]:border-chariot-stroke-blue rounded-none border-b-2 border-transparent px-4 py-2 text-xs font-medium data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  {isSelected && (
                    <Check className="mr-1 inline size-3 text-emerald-500" />
                  )}
                  Option {i + 1}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {validOptions.map((option: ConstantinePatchOption, i: number) => {
          const optionId = option.id ?? `option-${i}`;
          const isSelected = selectedOptionId === optionId;
          const filePaths = option.diff ? parseFilePaths(option.diff) : [];

          return (
            <TabsContent key={i} value={String(i)} className="mt-0 px-4 py-3">
              <div className="text-chariot-text-primary mb-2 text-sm font-medium">
                {option.strategy}
              </div>

              {option.description && (
                <div className="text-chariot-text-secondary mb-3 text-xs">
                  {option.description}
                </div>
              )}

              {option.defense_layers && option.defense_layers.length > 0 && (
                <div className="mb-3">
                  {option.defense_layers.map((layer, li) => (
                    <div
                      key={li}
                      className="text-chariot-text-secondary flex items-start gap-1 text-xs"
                    >
                      <span className="mt-0.5 shrink-0 font-medium capitalize">
                        {layer.layer}:
                      </span>
                      <span>{layer.description}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 flex items-center gap-2">
                {option.diff && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="text-chariot-stroke-blue cursor-pointer text-xs font-medium hover:opacity-80">
                        View Diff
                      </button>
                    </DialogTrigger>
                    <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col overflow-hidden">
                      <DialogHeader className="flex flex-row items-center justify-between">
                        <DialogTitle className="text-sm font-medium">
                          {option.strategy}
                        </DialogTitle>
                      </DialogHeader>
                      {filePaths.length > 0 && (
                        <div className="border-chariot-stroke bg-chariot-background-secondary flex items-center justify-between border-b px-3 py-2">
                          <div className="flex flex-col gap-0.5">
                            {filePaths.map((fp, idx) => (
                              <code
                                key={idx}
                                className="text-chariot-text-secondary font-mono text-xs"
                              >
                                {fp}
                              </code>
                            ))}
                          </div>
                          <CopyToClipboard textToCopy={option.diff}>
                            <button className="text-chariot-stroke-blue flex items-center gap-1 text-xs hover:opacity-80">
                              <ClipboardDocumentIcon className="size-3" />
                              Copy
                            </button>
                          </CopyToClipboard>
                        </div>
                      )}
                      {filePaths.length === 0 && (
                        <div className="border-chariot-stroke flex justify-end border-b px-3 py-2">
                          <CopyToClipboard textToCopy={option.diff}>
                            <button className="text-chariot-stroke-blue flex items-center gap-1 text-xs hover:opacity-80">
                              <ClipboardDocumentIcon className="size-3" />
                              Copy diff
                            </button>
                          </CopyToClipboard>
                        </div>
                      )}
                      <div
                        className={cn(
                          'border-chariot-stroke bg-chariot-background-primary overflow-auto rounded border',
                          !expandedDiffs[i] && 'max-h-[50vh]'
                        )}
                      >
                        <table className="w-full border-collapse font-mono text-xs">
                          <tbody>
                            {(() => {
                              let oldLine = 0;
                              let newLine = 0;
                              return option.diff
                                .split('\n')
                                .map((line, lineIdx) => {
                                  const isRemoved =
                                    line.startsWith('-') &&
                                    !line.startsWith('---');
                                  const isAdded =
                                    line.startsWith('+') &&
                                    !line.startsWith('+++');
                                  const isHunk = line.startsWith('@@');
                                  const isHeader =
                                    line.startsWith('---') ||
                                    line.startsWith('+++');

                                  if (isHunk) {
                                    const match = line.match(
                                      /@@ -(\d+)(?:,\d+)? \+(\d+)/
                                    );
                                    if (match) {
                                      oldLine = parseInt(match[1], 10);
                                      newLine = parseInt(match[2], 10);
                                    }
                                  }

                                  let oldNum: number | null = null;
                                  let newNum: number | null = null;
                                  if (!isHunk && !isHeader) {
                                    if (isRemoved) {
                                      oldNum = oldLine++;
                                    } else if (isAdded) {
                                      newNum = newLine++;
                                    } else {
                                      oldNum = oldLine++;
                                      newNum = newLine++;
                                    }
                                  }

                                  return (
                                    <tr
                                      key={lineIdx}
                                      className={cn(
                                        isRemoved && 'bg-red-500/10',
                                        isAdded && 'bg-emerald-500/10',
                                        isHunk && 'bg-blue-500/10'
                                      )}
                                    >
                                      <td className="text-chariot-text-secondary border-chariot-stroke w-10 border-r px-2 py-0 text-right align-top select-none">
                                        {oldNum ?? ''}
                                      </td>
                                      <td className="text-chariot-text-secondary border-chariot-stroke w-10 border-r px-2 py-0 text-right align-top select-none">
                                        {newNum ?? ''}
                                      </td>
                                      <td className="px-3 py-0 break-all whitespace-pre-wrap">
                                        <span
                                          className={cn(
                                            isRemoved && 'text-red-400',
                                            isAdded && 'text-emerald-400',
                                            isHunk && 'text-blue-400',
                                            isHeader &&
                                              'text-chariot-text-secondary font-medium',
                                            !isRemoved &&
                                              !isAdded &&
                                              !isHunk &&
                                              !isHeader &&
                                              'text-chariot-text-primary'
                                          )}
                                        >
                                          {line}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                });
                            })()}
                          </tbody>
                        </table>
                      </div>
                      {option.diff.split('\n').length > 30 && (
                        <button
                          onClick={() =>
                            setExpandedDiffs(prev => ({
                              ...prev,
                              [i]: !prev[i],
                            }))
                          }
                          className="text-chariot-stroke-blue text-xs font-medium hover:opacity-80"
                        >
                          {expandedDiffs[i] ? 'Collapse' : 'Show all'}
                        </button>
                      )}
                    </DialogContent>
                  </Dialog>
                )}

                {riskKey && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleSelect(option, i)}
                      disabled={isSelecting || isSelected || isClearing}
                      size="sm"
                      variant={isSelected ? 'outline' : 'default'}
                      className={cn(
                        'h-7 rounded-full text-xs',
                        isSelected && 'border-emerald-600 text-emerald-600'
                      )}
                    >
                      {isSelected ? (
                        <>
                          <Check className="mr-1 size-3" />
                          Selected
                        </>
                      ) : (
                        'Select this patch'
                      )}
                    </Button>
                    {isSelected && (
                      <Button
                        onClick={async () => {
                          if (!riskKey) return;
                          await clearPatch(riskKey, findingId);
                          setSelectedOptionId(null);
                          setPrLink(null);
                        }}
                        disabled={isClearing}
                        size="sm"
                        variant="ghost"
                        className="text-chariot-text-secondary h-7 rounded-full text-xs"
                      >
                        {isClearing ? 'Clearing...' : 'Deselect'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Create PR button -- visible when patch is selected and risk is from github.com */}
      {selectedOptionId && isGitHubRisk && (
        <div className="border-chariot-stroke border-t px-4 py-3">
          {prLink ? (
            <div className="flex items-center gap-2">
              <a
                href={prLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-chariot-stroke-blue flex items-center gap-1 text-xs font-medium hover:opacity-80"
              >
                <ArrowTopRightOnSquareIcon className="size-4" />
                View PR #{prLink.split('/').pop()}
              </a>
              {persistedSelection?.pr_status && (
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    persistedSelection.pr_status === 'pr_merged'
                      ? 'bg-purple-500/10 text-purple-400'
                      : persistedSelection.pr_status === 'pr_closed'
                        ? 'bg-chariot-text-secondary/10 text-chariot-text-secondary'
                        : 'bg-chariot-stroke-blue/10 text-chariot-stroke-blue'
                  )}
                >
                  {persistedSelection.pr_status === 'pr_created'
                    ? 'Open'
                    : persistedSelection.pr_status === 'pr_merged'
                      ? 'Merged'
                      : persistedSelection.pr_status === 'pr_closed'
                        ? 'Closed'
                        : persistedSelection.pr_status.replace('pr_', '')}
                </span>
              )}
            </div>
          ) : (
            <Button
              onClick={handleCreatePR}
              disabled={isCreatingPR || createFailed}
              size="sm"
              variant={createFailed ? 'destructive' : 'default'}
              className="h-7 rounded-full text-xs"
            >
              {isCreatingPR
                ? 'Creating PR...'
                : createFailed
                  ? 'PR Creation Failed'
                  : 'Create Pull Request'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
